// Catalog.ID Client — Identity Picker Component
// ============================================================================
// Shell action bar element for switching between linked identities.
// Uses createDropdownMenu from the shell.

import { createElement, createDropdownMenu } from '@catalog-shell';
import type { Shell } from '@catalog-shell';
import * as delegateApi from '../api/delegate';
import { identity } from '../state/identity';
import * as deviceStorage from '../state/device-storage';
import { api } from '../api/client';
import type { LinkedIdentity } from '../api/types';
import { originAvailability } from '../state/origins';

const TYPE_LABELS: Record<string, string> = {
    natural_person: 'Personal',
    organization: 'Organization',
    delegate: 'Delegate',
    agent: 'Agent',
};

const TYPE_COLORS: Record<string, string> = {
    natural_person: 'cornflowerblue',
    delegate: '#e89040',
    organization: '#50c878',
    agent: '#b080e0',
};

export function createIdentityPicker(shell: Shell): HTMLElement {
    const container = createElement('div', { className: 'cid-identity-picker' });

    const active = identity.getActive();
    if (!active) return container;

    // Label showing active CID + type
    const label = createElement('span', { className: 'cid-identity-picker-label' });
    label.textContent = active.cid;

    const badge = createElement('span', { className: 'cid-identity-picker-badge' });
    badge.textContent = TYPE_LABELS[active.accountType] ?? active.accountType;
    badge.style.color = TYPE_COLORS[active.accountType] ?? '#aaa';

    void loadAndRenderPicker(container, label, badge, shell);

    return container;
}

async function loadAndRenderPicker(
    container: HTMLElement,
    label: HTMLElement,
    badge: HTMLElement,
    _shell: Shell,
): Promise<void> {
    let identities: LinkedIdentity[] = [];

    try {
        identities = await delegateApi.getLinkedIdentities();
        identity.setLinkedIdentities(identities);
    } catch {
        container.appendChild(label);
        container.appendChild(badge);
        return;
    }

    if (identities.length <= 1) {
        container.appendChild(label);
        container.appendChild(badge);
        return;
    }

    // Multiple identities — create dropdown. Each identity is used only at its
    // own origin (docs/cid-governance.md D18, M7a), and one dashboard can hold
    // identities from several nodes: ask, for every other identity, whether its
    // origin answers, and grey the ones whose origin does not.
    const active = identity.getActive();
    const unavailable = await unavailableIdentities(identities.filter(id => id.cid !== active?.cid));
    container.appendChild(buildIdentityDropdown(active, identities, badge, switchIdentity, unavailable));
}

/**
 * The identities whose origin cannot be used right now, with the origin's code
 * when it is known. Probed in parallel; each probe is bounded by a timeout
 * (`originAvailability`), so a dead node greys its identities instead of
 * holding the picker up.
 */
async function unavailableIdentities(identities: LinkedIdentity[]): Promise<Map<string, string | undefined>> {
    const answers = await Promise.all(identities.map(async id => ({ cid: id.cid, answer: await originAvailability(id.cid) })));
    const unavailable = new Map<string, string | undefined>();
    for (const { cid, answer } of answers) {
        if (!answer.available) unavailable.set(cid, answer.code);
    }
    return unavailable;
}

/**
 * The labelled dropdown for two or more linked identities: the active CID as
 * the trigger label with its type badge riding inside the trigger, one item
 * per identity (the active one disabled). Exported for tests; `onSwitch` is
 * `switchIdentity` in the app.
 */
export function buildIdentityDropdown(
    active: Pick<LinkedIdentity, 'cid'> | null,
    identities: LinkedIdentity[],
    badge: HTMLElement,
    onSwitch: (linked: LinkedIdentity) => void,
    /** Identities whose origin is unreachable → the origin's code, when known.
     *  Greyed, never switched to: there is no other node to fall back on. */
    unavailable: ReadonlyMap<string, string | undefined> = new Map(),
): HTMLElement {
    const items = identities.map(id => {
        const label = `${id.cid} (${TYPE_LABELS[id.accountType] ?? id.accountType})`;
        if (unavailable.has(id.cid) && id.cid !== active?.cid) {
            const code = unavailable.get(id.cid);
            return {
                label: `${label} — home server unreachable`,
                onClick: () => onSwitch(id),
                disabled: true,
                title: code
                    ? `This identity's home server (${code}) cannot be reached right now. Try again later.`
                    : 'This identity\'s home server cannot be reached right now. Try again later.',
            };
        }
        return { label, onClick: () => onSwitch(id), disabled: id.cid === active?.cid };
    });

    const dropdown = createDropdownMenu({
        label: `${active?.cid ?? ''}`,
        items,
    });

    // `dropdown.element` IS the trigger (a labelled <button>); the badge goes
    // straight into it. (A querySelector for the trigger from here finds
    // nothing — that is how the badge went missing for a long time.)
    dropdown.element.appendChild(badge);
    return dropdown.element;
}

/**
 * Switch the active identity and reload into it.
 *
 * Exported because other surfaces send the user "into" an identity too — the
 * org workspace's Recovery Kit pane hands off to the organization identity,
 * where Settings already offers Reissue and Test. They must all agree on what
 * switching means: pick the stored session token for the target CID, record the
 * active CID for device unlock, then do a full reload so the shell rebuilds
 * against the new identity.
 */
export function switchIdentity(linked: LinkedIdentity): void {
    // Switch to the target identity's session token
    const hasSession = api.switchToCid(linked.cid);
    if (!hasSession) {
        // No stored session for this identity — user needs to log in
        // Clear current state and redirect to login
        deviceStorage.setActiveCid(linked.cid);
        window.location.href = '/';
        return;
    }

    // Update active CID in localStorage (used by device unlock on page load)
    deviceStorage.setActiveCid(linked.cid);

    // Full page reload — the app will use the new activeCid to find the right
    // session token and device keys, then re-initialize the shell with correct data
    window.location.href = '/';
}
